<template>
  <div class="foodleap-ir-page antialiased pb-24">

    <!-- Hero Section -->
    <section class="pt-24 pb-20 ir-bg-light relative overflow-hidden">
      <div class="max-w-4xl mx-auto px-8 text-center relative z-10">
        <span class="ir-badge tw-inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-6 animate-on-scroll">
          QUARTERLY INTELLIGENCE
        </span>
        <h1 class="text-5xl md:text-7xl font-bold tracking-tight ir-text-slate mb-8 leading-[1.1] animate-on-scroll delay-1 mt-6">
          Innovation Radar
        </h1>
        <p class="text-xl ir-text-slate-70 font-light max-w-2xl mx-auto mb-12 animate-on-scroll delay-2">
          Every quarter, we map the competitive landscape of food innovation, plotting where organisations are positioned on scale readiness and strategic impact.
        </p>
        <div class="tw-flex justify-center animate-on-scroll delay-3">
          <button
            @click="$router.push('/intelligence')"
            class="ir-btn-primary tw-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-lg">
            Get access
            <Icon name="arrow-right"/>
          </button>
        </div>
      </div>
      <div class="ir-blob absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-20"></div>
      <div class="ir-blob-2 absolute -bottom-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-15"></div>
    </section>

    <!-- How to read the radar -->
    <section class="py-20 bg-white">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-14 animate-on-scroll">
          <h2 class="text-4xl font-bold ir-text-slate mb-4">How to read the radar</h2>
          <p class="ir-text-slate-60 text-lg font-light">Three dimensions that reveal where organisations truly stand.</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="ir-axis-card animate-on-scroll">
            <div class="ir-axis-icon w-12 h-12 rounded-xl tw-flex items-center justify-center mb-5">
              <Icon name="shuffle-variant" size="20"/>
            </div>
            <div class="ir-axis-label text-xs font-bold tracking-widest uppercase mb-2">X Axis</div>
            <h3 class="text-xl font-bold ir-text-slate mb-3">Scale Readiness</h3>
            <p class="ir-text-slate-60 font-light leading-relaxed text-sm">
              Measures the organisation's position along the scaling journey from early-stage concept through piloting, commercialising, and ultimately orchestrating at
              industrial scale.
            </p>
          </div>
          <div class="ir-axis-card animate-on-scroll delay-1">
            <div class="ir-axis-icon w-12 h-12 rounded-xl tw-flex items-center justify-center mb-5">
              <Icon name="sort" size="20"/>
            </div>
            <div class="ir-axis-label text-xs font-bold tracking-widest uppercase mb-2">Y Axis</div>
            <h3 class="text-xl font-bold ir-text-slate mb-3">Strategic Impact</h3>
            <p class="ir-text-slate-60 font-light leading-relaxed text-sm">
              Reflects the breadth of influence across the food system from niche applications serving specific verticals through to critical infrastructure shaping entire
              sectors.
            </p>
          </div>
          <div class="ir-axis-card animate-on-scroll delay-2">
            <div class="ir-axis-icon w-12 h-12 rounded-xl tw-flex items-center justify-center mb-5">
              <Icon name="bullseye" size="20"/>
            </div>
            <div class="ir-axis-label text-xs font-bold tracking-widest uppercase mb-2">Bubble Size</div>
            <h3 class="text-xl font-bold ir-text-slate mb-3">Orchestration Score</h3>
            <p class="ir-text-slate-60 font-light leading-relaxed text-sm">
              The diameter of each bubble encodes the organisation's ecosystem influence score how many partnerships, collaborations, and knowledge flows pass through it.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Sample radar preview -->
    <section class="py-20 ir-bg-light-grey">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-12 animate-on-scroll">
          <h2 class="text-4xl font-bold ir-text-slate mb-4">Preview: Q1 2026 Radar</h2>
          <p class="ir-text-slate-60 text-lg font-light">A sample of the quarterly positioning map, hover dots to reveal organisations.</p>
        </div>
        <div class="animate-on-scroll">
          <!-- Radar circle container -->
          <div class="relative mx-auto" style="max-width: 600px;">
            <div
              class="relative w-full rounded-full"
              style="padding-top:100%;background:radial-gradient(circle,rgba(248,250,251,0.95) 0%,rgba(217,226,218,0.2) 60%,#f0f4f0 100%);border:1px solid rgba(217,226,218,0.7);"
            >
              <div class="absolute inset-0">
                <!-- Concentric rings -->
                <div class="absolute inset-0 tw-flex items-center justify-center pointer-events-none">
                  <div class="absolute rounded-full" style="width:80%;height:80%;border:1px solid rgb(213 213 213);"/>
                  <div class="absolute rounded-full" style="width:60%;height:60%;border:1px solid rgb(213 213 213);"/>
                  <div class="absolute rounded-full" style="width:40%;height:40%;border:1px solid rgb(213 213 213);"/>
                  <div class="absolute rounded-full" style="width:20%;height:20%;border:1px solid rgb(213 213 213);"/>
                </div>
                <!-- Axis lines -->
                <div class="absolute" style="top:50%;left:0;right:0;height:1px;background:rgb(213 213 213);"/>
                <div class="absolute" style="left:50%;top:0;bottom:0;width:1px;background:rgb(213 213 213);"/>
                <!-- Axis labels -->
                <span class="absolute pointer-events-none"
                      style="top:50%;left:8px;transform:translateY(-50%);font-size:9px;color:rgba(33,36,74,0.35);font-weight:600;font-family:Inter,sans-serif;">Early</span>
                <span class="absolute pointer-events-none"
                      style="top:50%;right:8px;transform:translateY(-50%);font-size:9px;color:rgba(33,36,74,0.35);font-weight:600;font-family:Inter,sans-serif;">Orchestrating</span>
                <span class="absolute pointer-events-none"
                      style="top:8px;left:50%;transform:translateX(-50%);font-size:9px;color:rgba(33,36,74,0.35);font-weight:600;font-family:Inter,sans-serif;">Critical</span>
                <span class="absolute pointer-events-none"
                      style="bottom:8px;left:50%;transform:translateX(-50%);font-size:9px;color:rgba(33,36,74,0.35);font-weight:600;font-family:Inter,sans-serif;">Niche</span>
                <!-- Dots -->
                <div
                  v-for="dot in pricingDots"
                  :key="dot.actorId + '-' + dot.label"
                  class="absolute rounded-full ir-dot"
                  :style="{
                    left: dot.x + '%',
                    top: (100 - dot.y) + '%',
                    width: (dot.size + 2) + 'px',
                    height: (dot.size + 2) + 'px',
                    backgroundColor: pricingVcColor(dot.label),
                    transform: hoveredPricingDot && hoveredPricingDot.actorId === dot.actorId ? 'translate(-50%,-50%) scale(1.8)' : 'translate(-50%,-50%)',
                    zIndex: hoveredPricingDot && hoveredPricingDot.actorId === dot.actorId ? 20 : 1,
                    opacity: 0.85,
                    transition: 'transform 0.15s',
                  }"
                  @mouseenter="hoveredPricingDot = dot"
                  @mouseleave="hoveredPricingDot = null"
                />
                <!-- Tooltip -->
                <div
                  v-if="hoveredPricingDot"
                  class="absolute z-30 pointer-events-none rounded-lg shadow-lg"
                  style="padding:3px 8px;font-size:10px;font-weight:600;background:rgba(33,36,74,0.92);white-space:nowrap;font-family:Inter,sans-serif;color:white;"
                  :style="pricingTooltipStyle(hoveredPricingDot)"
                >
                  {{ hoveredPricingDot.name }}
                </div>
              </div>
            </div>
          </div>
          <!-- Legend -->
          <div class="tw-flex flex-wrap justify-center gap-4 mt-6">
            <div v-for="cat in pricingCategories" :key="cat.label" class="tw-flex items-center gap-1.5">
              <div class="rounded-full w-2.5 h-2.5 shrink-0" :style="{ backgroundColor: cat.color }"></div>
              <span class="text-xs ir-text-slate-60 font-medium">{{ cat.label }}</span>
            </div>
          </div>
          <!-- Sub-caption -->
          <div class="text-center mt-10">
            <p class="ir-text-slate-60 mb-6 font-light">Subscribe to access the full interactive radar with filters and actor profiles.</p>
            <!--            <button
                          @click="$router.push('/intelligence')"
                          class="ir-btn-primary tw-inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold">
                          Unlock the full radar
                          <Icon name="arrow-right"/>
                        </button>-->
          </div>
        </div>
      </div>
    </section>

    <!-- Included topics -->
    <section class="py-20 bg-white">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-12 animate-on-scroll">
          <h2 class="text-4xl font-bold ir-text-slate mb-4">Included topics</h2>
          <p class="ir-text-slate-60 text-lg font-light">Every radar covers six innovation verticals across the food transition.</p>
        </div>
        <div class="tw-flex flex-wrap justify-center gap-3 animate-on-scroll delay-1">
          <span
            v-for="topic in topics"
            :key="topic"
            class="ir-topic-badge px-5 py-2.5 rounded-full text-sm font-semibold">
            {{ topic }}
          </span>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="py-24 ir-bg-slate">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-10 animate-on-scroll">
          <h2 class="text-3xl font-bold text-white mb-3">Get full access</h2>
          <p class="text-white/50 font-light">Everything you need to follow the food system transition.</p>
        </div>
        <div class="grid md:grid-cols-3 gap-6 items-center">
          <!-- Innovation Challenges -->
          <div class="ir-price-card rounded-2xl p-8 animate-on-scroll">
            <h3 class="text-base font-bold text-white mb-1">Innovation Challenges</h3>
            <p class="text-white/40 text-xs mb-6">Co-design open innovation with startups</p>
            <div class="mb-6">
              <span class="text-3xl font-black text-white">€7,500</span>
              <span class="text-white/40 text-sm ml-1">/ challenge</span>
            </div>
            <ul class="space-y-2 mb-8">
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="ir-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Ecosystem search</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="ir-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Partner matchmaking</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="ir-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Managed pilot design</span>
              </li>
            </ul>
            <button @click="$router.push({ path: '/about', hash: '#contact' })" class="ir-btn-ghost w-full py-2.5 rounded-full font-semibold text-sm">
              Talk to us
            </button>
          </div>

          <!-- Ecosystem Insights (highlighted) -->
          <div class="ir-price-card ir-price-card-featured rounded-2xl p-10 relative animate-on-scroll delay-1">
            <div class="ir-popular-badge absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black tracking-widest uppercase">
              Most Popular
            </div>
            <h3 class="text-xl font-bold text-white text-center mb-1 mt-6">Ecosystem Insights</h3>
            <p class="text-white/40 text-sm text-center mb-8">All transition reports + live ecosystem data</p>
            <div class="text-center mb-8">
              <span class="text-5xl font-black text-white">€9,000</span>
              <span class="text-white/40 text-base ml-2">/ year</span>
            </div>
            <ul class="space-y-3 mb-10">
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="ir-check-icon shrink-0"/>
                <span class="text-white text-sm">Transition Pulse — monthly</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="ir-check-icon shrink-0"/>
                <span class="text-white text-sm">Innovation Radar — quarterly</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="ir-check-icon shrink-0"/>
                <span class="text-white text-sm">Deep Dive Reports — annual</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="ir-check-icon shrink-0"/>
                <span class="text-white text-sm">Full ecosystem data access</span>
              </li>
            </ul>
            <button disabled class="ir-btn-green w-full py-4 rounded-full font-bold text-base cursor-not-allowed opacity-50">Coming soon</button>
          </div>

          <!-- Transition Advisory -->
          <div class="ir-price-card rounded-2xl p-8 animate-on-scroll delay-2">
            <h3 class="text-base font-bold text-white mb-1">Transition Advisory</h3>
            <p class="text-white/40 text-xs mb-6">Bespoke strategy for your journey</p>
            <div class="mb-6">
              <span class="text-3xl font-black text-white">Project</span>
              <span class="text-white/40 text-sm ml-1">based</span>
            </div>
            <ul class="space-y-2 mb-8">
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="ir-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Bespoke deep dives</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="ir-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Strategy workshop</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="ir-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Executive coaching</span>
              </li>
            </ul>
            <button @click="$router.push({ path: '/about', hash: '#contact' })" class="ir-btn-ghost w-full py-2.5 rounded-full font-semibold text-sm">
              Talk to us
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Report Switcher Bar -->
    <div class="ir-rs-bar fixed bottom-6 left-1/2 -translate-x-1/2 z-50 tw-flex items-center gap-1 px-2 py-2 rounded-full shadow-2xl">
      <button @click="$router.push('/transition-pulse-report')" class="ir-rs-tab tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="wave-square" size="13"/>
        Pulse
      </button>
      <button @click="$router.push('/innovation-radar-report')" class="ir-rs-tab ir-rs-active tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="bullseye" size="13"/>
        Radar
      </button>
      <button @click="$router.push('/deep-dive-report')" class="ir-rs-tab tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="file-text" size="13"/>
        Deep Dive
      </button>
    </div>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import Icon from '../Icon/Icon.vue'

  export default defineComponent({
    name: 'StaticHomeFoodleapInnovationRadarPage',
    components: { SimplifiedFooterFoodleapNew, Icon },

    data () {
      return {
        hoveredPricingDot: null,
        pricingActors: [
          { actorId: 1256725, name: 'PPM Oost NV', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 69, y: 46.8, size: 8 }] },
          { actorId: 1650662, name: 'Alvinesa', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 72, y: 70.6, size: 6 }] },
          { actorId: 1518971, name: 'Royal Smilde', valueChainPositions: [{ label: 'End Products & Brands', x: 68.6, y: 41, size: 12 }] },
          { actorId: 1520366, name: 'MOA', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 59.4, y: 65.6, size: 4 }] },
          { actorId: 1347894, name: 'Bodec', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 67.4, y: 54.4, size: 6 }] },
          { actorId: 1250519, name: 'Biorefinery Solutions', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 54, y: 60, size: 4 }] },
          { actorId: 1643161, name: 'Koa', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 71.4, y: 65.6, size: 10 }] },
          { actorId: 1643159, name: 'Instock Products B.V.', valueChainPositions: [{ label: 'Market Access & Distribution', x: 80.6, y: 76.6, size: 6 }] },
          { actorId: 1650677, name: 'FLUID QUIP TECHNOLOGIES', valueChainPositions: [{ label: 'Processing & Technology', x: 78, y: 57.8, size: 6 }] },
          { actorId: 1256629, name: 'Agrifirm NWE B.V.', valueChainPositions: [{ label: 'Processing & Technology', x: 74.6, y: 54.4, size: 14 }] },
          { actorId: 1256747, name: 'Greencovery®', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 61.2, y: 65.6, size: 4 }] },
          { actorId: 1442798, name: 'Grassa', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 69.4, y: 64.4, size: 6 }] },
          { actorId: 1256729, name: 'Provincie Gelderland', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 70.6, y: 71.2, size: 14 }] },
          { actorId: 1639571, name: 'B2BE Facilitator', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 50.6, y: 51.2, size: 4 }] },
          { actorId: 1652736, name: 'Nature Preserve', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 68.6, y: 53.4, size: 4 }] },
          { actorId: 1256746, name: 'EverGrain Ingredients', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 74.6, y: 65.6, size: 6 }] },
          { actorId: 1442803, name: 'PeelPioneers', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 80.6, y: 81.6, size: 6 }] },
          { actorId: 1334892, name: 'Legria', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 65.4, y: 70, size: 4 }] },
          { actorId: 1652746, name: 'Beyond Leather Materials', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 71.4, y: 60, size: 4 }] },
          { actorId: 1381215, name: 'Danish Technological Institute', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 71.4, y: 67.8, size: 14 }] },
          { actorId: 1272305, name: 'Carbiotix', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 63.4, y: 62.2, size: 4 }] },
          { actorId: 1367904, name: 'Green Spot Technologies', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 66.6, y: 61.6, size: 6 }] },
          { actorId: 1256641, name: 'Herbafood Ingredients GmbH', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 72.6, y: 56.6, size: 8 }] },
          { actorId: 1643036, name: 'Kaffe Bueno', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 78, y: 73.4, size: 6 }] },
          { actorId: 1304668, name: 'Feedvalid', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 77, y: 62.2, size: 8 }] },
          { actorId: 1304672, name: 'NoPalm Ingredients', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 73.4, y: 61.2, size: 6 }] },
          { actorId: 1643162, name: 'Kumasi', valueChainPositions: [{ label: 'End Products & Brands', x: 76, y: 64.4, size: 6 }] },
          { actorId: 1382332, name: 'Re:Start Upcycling', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 50.6, y: 53.4, size: 4 }] },
          { actorId: 1425929, name: 'InsectSense', valueChainPositions: [{ label: 'Processing & Technology', x: 55, y: 42.8, size: 4 }] },
          { actorId: 1652800, name: 'Hub cycle', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 67.6, y: 65.6, size: 6 }] },
          { actorId: 1652803, name: 'Prosur', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 70.6, y: 53.4, size: 6 }] },
          { actorId: 1518809, name: 'FrieslandCampina', valueChainPositions: [{ label: 'End Products & Brands', x: 82.6, y: 58.4, size: 14 }] },
          { actorId: 1256642, name: 'International Flavors & Fragrances', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 86, y: 62.2, size: 14 }] },
          { actorId: 1490324, name: 'KiCo', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 76, y: 51.6, size: 4 }] },
          { actorId: 1352994, name: 'NOI Techpark', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 86, y: 63.4, size: 10 }] },
          { actorId: 1334890, name: 'Meatco', valueChainPositions: [{ label: 'Processing & Technology', x: 62, y: 60, size: 6 }] },
          { actorId: 1342731, name: 'Bakkersgrondstof', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 56.6, y: 63.4, size: 4 }] },
          { actorId: 1503209, name: 'Van Kneus', valueChainPositions: [{ label: 'End Products & Brands', x: 47, y: 43.8, size: 4 }] },
          { actorId: 1604510, name: 'Maltivor', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 80.4, y: 62.2, size: 6 }] },
          { actorId: 1616400, name: 'Upgrain', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 78, y: 72.8, size: 6 }] },
          { actorId: 1643039, name: 'Kost Capital', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 53.4, y: 53.8, size: 4 }] },
          { actorId: 1639730, name: 'SomaTech', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 60.4, y: 57.8, size: 4 }] },
          { actorId: 1639567, name: 'RE:harvest', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 72.6, y: 65.6, size: 6 }] },
          { actorId: 1618331, name: 'Pacha de Cacao', valueChainPositions: [{ label: 'End Products & Brands', x: 65, y: 60, size: 4 }] },
          { actorId: 1643038, name: 'endless food', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 66.6, y: 63.4, size: 4 }] },
          { actorId: 1378500, name: 'Caffe Inc', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 77, y: 67.8, size: 4 }] },
          { actorId: 1386434, name: 'Samen Tegen Voedselverspilling', valueChainPositions: [{ label: 'Market Access & Distribution', x: 68.6, y: 53.8, size: 4 }] },
          { actorId: 1252694, name: 'Uticon (Netherlands)', valueChainPositions: [{ label: 'Processing & Technology', x: 78, y: 56.6, size: 8 }] },
          { actorId: 1553372, name: 'Team Tastemakers', valueChainPositions: [{ label: 'End Products & Brands', x: 54.6, y: 46.6, size: 4 }] },
          { actorId: 1252573, name: 'PRIMEPACT', valueChainPositions: [{ label: 'Market Access & Distribution', x: 69.4, y: 36.6, size: 6 }] },
          { actorId: 1256711, name: 'Symrise', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 84.6, y: 66.6, size: 14 }] },
          { actorId: 1643122, name: 'ChainCraft', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 78, y: 70, size: 6 }] },
          { actorId: 1304674, name: 'Milgro', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 82, y: 58.8, size: 8 }] },
          { actorId: 1581013, name: 'Ingredalia', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 59, y: 62.2, size: 4 }] },
          { actorId: 1613866, name: 'Saledo', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 73.4, y: 72, size: 6 }] },
          { actorId: 1643042, name: 'Eat Wasted', valueChainPositions: [{ label: 'End Products & Brands', x: 67.6, y: 60, size: 6 }] },
          { actorId: 1634231, name: 'PXL8 Powders', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 46.6, y: 56.6, size: 6 }] },
          { actorId: 1634233, name: 'Revive Ingredients', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 70, y: 58.8, size: 6 }] },
          { actorId: 1639562, name: 'Sidestream', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 55.4, y: 53.4, size: 6 }] },
          { actorId: 1639563, name: 'Axians', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 84.6, y: 14.4, size: 8 }] },
          { actorId: 1639569, name: 'Trobas Gelatine BV', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 69, y: 53.8, size: 8 }] },
          { actorId: 1639572, name: 'ONE\\THIRD', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 79.4, y: 60, size: 4 }] },
          { actorId: 1639788, name: 'Upcycling Solutions', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 58.4, y: 65.6, size: 4 }] },
          { actorId: 1652802, name: 'Motai Group', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 40, y: 56.6, size: 4 }] },
          { actorId: 1640519, name: 'Packtin', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 58.6, y: 61.2, size: 4 }] },
          { actorId: 1641266, name: 'GroundUp eco-ventures', valueChainPositions: [{ label: 'End Products & Brands', x: 60.4, y: 66.2, size: 4 }] },
          { actorId: 1644260, name: 'Grounds For Good', valueChainPositions: [{ label: 'End Products & Brands', x: 67.6, y: 61.2, size: 4 }] },
          { actorId: 1643187, name: 'Bakers & Bananas', valueChainPositions: [{ label: 'End Products & Brands', x: 69.4, y: 58.8, size: 6 }] },
          { actorId: 1643046, name: 'No Waste Army', valueChainPositions: [{ label: 'End Products & Brands', x: 75, y: 62.2, size: 4 }] },
          { actorId: 1643852, name: 'Solid Fermentation Innovation', valueChainPositions: [{ label: 'Processing & Technology', x: 60, y: 55.6, size: 6 }] },
          { actorId: 1643192, name: 'Van Eigen Deeg', valueChainPositions: [{ label: 'End Products & Brands', x: 74.6, y: 50, size: 4 }] },
          { actorId: 1652804, name: 'Ex-Seed', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 58, y: 68.8, size: 4 }] },
          { actorId: 1645937, name: 'Spora', valueChainPositions: [{ label: 'Processing & Technology', x: 66, y: 63.8, size: 6 }] },
          { actorId: 1646103, name: 'Fed Up Caribbean Ltd.', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 42.6, y: 54.4, size: 6 }] },
          { actorId: 1647156, name: 'Food Hub SRL SB', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 62.6, y: 46.6, size: 4 }] },
          { actorId: 1650079, name: 'NICE BIOTECH CO. LTD.', valueChainPositions: [{ label: 'End Products & Brands', x: 35.4, y: 17.2, size: 6 }] },
          { actorId: 1650760, name: 'Biasol', valueChainPositions: [{ label: 'End Products & Brands', x: 66, y: 64.4, size: 4 }] },
          { actorId: 1651139, name: 'KTC Ingredients', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 62, y: 42.2, size: 6 }] },
          { actorId: 1652734, name: 'Mycotech', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 61.4, y: 60, size: 4 }] },
          { actorId: 1652735, name: 'Food Experts', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 58.6, y: 49.4, size: 8 }] },
          { actorId: 1652737, name: 'Nordinvent', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 67.4, y: 58.8, size: 4 }] },
          { actorId: 1652738, name: 'Kødbyens is', valueChainPositions: [{ label: 'End Products & Brands', x: 76.6, y: 31.2, size: 6 }] },
          { actorId: 1652742, name: 'Grounded', valueChainPositions: [{ label: 'End Products & Brands', x: 47.6, y: 53.4, size: 6 }] },
          { actorId: 1652741, name: 'Dana Technology', valueChainPositions: [{ label: 'Processing & Technology', x: 64, y: 40.2, size: 6 }] },
          { actorId: 1652743, name: 'Food Organisation of Denmark', valueChainPositions: [{ label: 'Market Access & Distribution', x: 66.6, y: 39.4, size: 6 }] },
          { actorId: 1652801, name: 'Ferm Labs', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 44.6, y: 44.4, size: 6 }] },
          { actorId: 1652745, name: 'Copenhagen Hospitality College', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 76.6, y: 51.6, size: 10 }] },
          { actorId: 1690963, name: 'Stockable BV', valueChainPositions: [{ label: 'Market Access & Distribution', x: 53.4, y: 53.4, size: 6 }] },
        ],
        topics: [
          'Alternative Proteins',
          'Fermentation Tech',
          'Circular Packaging',
          'Precision Fermentation',
          'Regenerative Inputs',
          'Waste Valorisation',
        ],
      }
    },

    computed: {
      pricingDots () {
        return this.pricingActors.flatMap(actor =>
          actor.valueChainPositions.map(pos => ({
            actorId: actor.actorId,
            name: actor.name,
            label: pos.label,
            x: pos.x,
            y: pos.y,
            size: Math.max(5, Math.min(14, pos.size)),
          }))
        )
      },
      pricingCategories () {
        const seen = new Set()
        const cats = []
        for (const dot of this.pricingDots) {
          if (!seen.has(dot.label)) {
            seen.add(dot.label)
            cats.push({ label: dot.label, color: this.pricingVcColor(dot.label) })
          }
        }
        return cats
      },
    },

    methods: {
      pricingVcColor (label) {
        const colors = {
          'Ingredients & Intermediates': '#5CCC61',
          'Enabling Infrastructure': '#70C1FF',
          'End Products & Brands': '#FFC83D',
          'Processing & Technology': '#B5F167',
          'Market Access & Distribution': '#EF374E',
        }
        return colors[label] || '#9ca3af'
      },
      pricingTooltipStyle (dot) {
        const x = dot.x
        const y = 100 - dot.y
        const flipX = x > 70 ? `right: calc(${100 - x}% + 10px)` : `left: calc(${x}% + 10px)`
        const flipY = y > 70 ? `bottom: calc(${100 - y}% + 4px)` : `top: calc(${y}% + 4px)`
        return flipX + '; ' + flipY
      },
    },

    mounted () {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this._observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.12 }
      )
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
        this._observer.observe(el)
      })
    },

    beforeUnmount () {
      if (this._observer) this._observer.disconnect()
    },
  })
</script>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .foodleap-ir-page {
    font-family: 'Inter', sans-serif;
    background-color: #ffffff;
    color: #21244A;

    // ── Brand color variables ──────────────────────────────────────────────────
    $ps: #21244A; // primary slate
    $green: #5CCC61; // primary green (accent for this page)
    $blg: #D9E2DA; // brand light grey
    $yellow: #FFC83D; // secondary yellow
    $blue: #70C1FF; // secondary blue
    $vg: #B5F167; // secondary vibrant green

    // ── Scroll-reveal ──────────────────────────────────────────────────────────
    .animate-on-scroll {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.6s ease, transform 0.6s ease;

      &.delay-1 {
        transition-delay: 0.12s;
      }

      &.delay-2 {
        transition-delay: 0.24s;
      }

      &.delay-3 {
        transition-delay: 0.36s;
      }

      &.is-visible {
        opacity: 1;
        transform: translateY(0);
      }
    }

    // ── Color utilities ────────────────────────────────────────────────────────
    .ir-text-slate {
      color: $ps;
    }

    .ir-text-slate-40 {
      color: rgba(33, 36, 74, 0.4);
    }

    .ir-text-slate-60 {
      color: rgba(33, 36, 74, 0.6);
    }

    .ir-text-slate-70 {
      color: rgba(33, 36, 74, 0.7);
    }

    .ir-bg-slate {
      background-color: $ps;
    }

    .ir-bg-light {
      background: radial-gradient(circle at top center, rgba(92, 204, 97, 0.07) 0%, #ffffff 70%);
    }

    .ir-bg-light-grey {
      background-color: rgba(217, 226, 218, 0.25);
    }

    // ── Hero ───────────────────────────────────────────────────────────────────
    .ir-badge {
      background-color: rgba(92, 204, 97, 0.12);
      color: darken($green, 10%);
      border: 1px solid rgba(92, 204, 97, 0.3);
    }

    .ir-blob {
      background-color: $green;
    }

    .ir-blob-2 {
      background-color: $blue;
    }

    // ── Buttons ────────────────────────────────────────────────────────────────
    .ir-btn-primary {
      background-color: $ps;
      color: #ffffff;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.2);
      }

      svg {
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }

    .ir-btn-green {
      background-color: $green;
      color: $ps;
      font-weight: 700;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(92, 204, 97, 0.4);
      }

      svg {
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }

    // ── Axis cards ─────────────────────────────────────────────────────────────
    .ir-axis-card {
      background-color: #ffffff;
      border: 1px solid $blg;
      border-radius: 1.25rem;
      padding: 2rem;
      transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.08);
        border-color: rgba(92, 204, 97, 0.4);
      }
    }

    .ir-axis-icon {
      background-color: rgba(92, 204, 97, 0.12);
      color: darken($green, 5%);
    }

    .ir-axis-label {
      color: $green;
    }

    // ── Radar ──────────────────────────────────────────────────────────────────
    .ir-radar-circle {
      border-color: rgba(92, 204, 97, 0.3);
      background: radial-gradient(circle at center, rgba(92, 204, 97, 0.04) 0%, rgba(217, 226, 218, 0.15) 100%);
    }

    .ir-dot {
      transition: transform 0.2s, box-shadow 0.2s;
      cursor: default;

      &:hover {
        transform: translate(-50%, -50%) scale(1.3);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        z-index: 10;
      }
    }

    // ── Pricing block ──────────────────────────────────────────────────────────
    .ir-price-card-featured {
      border-color: rgba(92, 204, 97, 0.4);
      box-shadow: 0 0 0 1px rgba(92, 204, 97, 0.2);
    }

    .ir-btn-ghost {
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: rgba(255, 255, 255, 0.6);
      transition: border-color 0.2s, color 0.2s;

      &:hover {
        border-color: rgba(255, 255, 255, 0.5);
        color: rgba(255, 255, 255, 0.9);
      }
    }

    .ir-price-card {
      background-color: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .ir-popular-badge {
      background-color: $green;
      color: $ps;
    }

    .ir-check-icon {
      color: $green;
      flex-shrink: 0;
    }

    // ── Report switcher bar ────────────────────────────────────────────────────
    .ir-rs-bar {
      background-color: $ps;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .ir-rs-tab {
      color: rgba(255, 255, 255, 0.45);
      transition: all 0.2s;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }

    .ir-rs-active {
      background-color: $green;
      color: $ps !important;
    }

    // ── Topic badges ───────────────────────────────────────────────────────────
    .ir-topic-badge {
      background-color: rgba(92, 204, 97, 0.1);
      color: darken($green, 8%);
      border: 1px solid rgba(92, 204, 97, 0.25);
      transition: transform 0.2s, background-color 0.2s, box-shadow 0.2s;
      cursor: default;

      &:hover {
        transform: translateY(-2px);
        background-color: rgba(92, 204, 97, 0.2);
        box-shadow: 0 4px 12px rgba(92, 204, 97, 0.2);
      }
    }
  }
</style>
